import React from 'react';

import { useClassnames } from '@lumapps/classnames';
import { Menu } from '@lumapps/lumx-menu/components/Menu';
import { Placement, Offset, GenericProps } from '@lumapps/lumx/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { UserAvatar } from '@lumapps/user-ui/components/UserAvatar';
import { USER } from '@lumapps/user/keys';
import { loginRoute } from '@lumapps/user/routes';
import { User } from '@lumapps/user/types';

import { useTopBarUserMenuLinks } from '../../hooks/useTopBarUserMenuLinks';
import { UserMenu } from '../UserMenu';

import './index.scss';

export interface UserProfileDropdownProps {
    /** The dropdown placement */
    placement?: Placement;
    /** The dropdown offset */
    offset?: Offset;
    /** whether this component is visible or not */
    isVisible?: boolean;
    /** is user connected or is it a guest */
    isUserConnected?: boolean;
    /** connected user */
    user?: User;
}

const CLASSNAME = 'user-profile-dropdown';
const SCOPE = 'user-profile';

const UserMenuTriggerButton = React.forwardRef(({ user, onClick, ...forwardedProps }: GenericProps, ref) => {
    const { translateKey } = useTranslate();
    const label = translateKey(GLOBAL.VIEW_USER_PROFILE);
    return (
        <UserAvatar
            ref={ref}
            // Hide the tooltip if the menu is open
            tooltipProps={{ label: forwardedProps['aria-expanded'] ? null : label }}
            user={user}
            scope={SCOPE}
            avatarAs="button"
            alt={label}
            onClick={onClick}
            // Making sure to forward menu trigger props to the button components
            buttonProps={{ ...forwardedProps, className: CLASSNAME }}
        />
    );
});

export const UserProfileDropdown: React.FC<UserProfileDropdownProps> = ({
    placement,
    offset = {},
    isVisible = true,
    isUserConnected,
    user,
}) => {
    const { element } = useClassnames(CLASSNAME);
    const { translateKey } = useTranslate();

    /** Should fetch user permissions only if dropdown is open */
    const [isUserMenuOpen, setIsUserMenuOpen] = React.useState(false);
    const { topBarUserMenuLinks } = useTopBarUserMenuLinks(isUserMenuOpen);

    if (!isVisible) {
        return null;
    }

    if (!isUserConnected) {
        return (
            <UserAvatar
                className={CLASSNAME}
                user={user}
                scope={SCOPE}
                avatarAs="link"
                linkUrl={loginRoute.path}
                tooltipProps={{
                    label: translateKey(USER.LOGIN_CLASSIC_SIGN_IN),
                }}
                alt={translateKey(USER.LOGIN_CLASSIC_SIGN_IN)}
            />
        );
    }

    if (user) {
        return (
            <Menu.Trigger onToggle={setIsUserMenuOpen} popoverProps={{ placement, offset, minWidth: 'xxl' }}>
                <Menu.TriggerButton as={UserMenuTriggerButton} user={user} />

                <Menu className={element('menu-content')}>
                    <UserMenu links={topBarUserMenuLinks} scope={SCOPE} user={user} />
                </Menu>
            </Menu.Trigger>
        );
    }

    return null;
};
